import React, { useState } from 'react';
import { Provider } from 'urql';
import { appWithTranslation } from 'next-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { DSAriaLiveRegions, DSToastProvider } from '@stihl-design-system/components';

import { ErrorLogger } from '@phoenix/core/frontend/shared/feature/error-logger';
import { Scaffold } from '@phoenix/core/frontend/shared/feature/scaffold/scaffold';
import { nextI18nConfig } from '@phoenix/core/frontend/shared/domain/i18n/i18n-config';
import { createGraphQLClient } from '@phoenix/core/frontend/shared/domain/http/graphql.service';

import { dashboardCspDirectives } from '@phoenix/team-m/dashboard/frontend/domain/security/csp';
import { AppAvailable } from '@phoenix/team-m/dashboard/frontend/shared/app-available';
import { UserPreferencesContextProvider } from '@phoenix/team-m/dashboard/frontend/shared/use-user-preferences-context';

import { CustomAppProps } from '../models/app.model';
import { Layout } from '../components/layout';

import './styles.scss';
import 'swiper/swiper-bundle.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

if (!process.env.NEXT_PUBLIC_DASHBOARD_GRAPHQL_BASE_URL) {
  throw new Error('NEXT_PUBLIC_DASHBOARD_GRAPHQL_BASE_URL is not defined');
}
const client = createGraphQLClient(process.env.NEXT_PUBLIC_DASHBOARD_GRAPHQL_BASE_URL);

function CustomApp({ Component, pageProps }: CustomAppProps) {
  const { header, footer, assets, navigation } = pageProps;
  const [ldContextReady, setLdContextReady] = useState(false);

  return (
    <ErrorLogger>
      <QueryClientProvider client={queryClient}>
        <DSAriaLiveRegions />
        <DSToastProvider>
          <Scaffold
            navigation={{ ...navigation, hide: true }}
            footer={footer}
            header={{ ...header, hide: true }}
            cspDirectives={dashboardCspDirectives}
            assets={assets}
            onLDContextReady={() => setLdContextReady(true)}
          >
            <AppAvailable ldContextReady={ldContextReady}>
              <Layout navItems={navigation?.navItems}>
                <Provider value={client}>
                  <UserPreferencesContextProvider>
                    <Component {...pageProps} />
                  </UserPreferencesContextProvider>
                </Provider>
              </Layout>
            </AppAvailable>
          </Scaffold>
        </DSToastProvider>
      </QueryClientProvider>
    </ErrorLogger>
  );
}

export default appWithTranslation(CustomApp, nextI18nConfig());
